<template>
  <!-- Page Content -->
  <div class="h-100 d-flex flex-column">
    <div class="row f-row justify-content-center h-100">
      <div class="col col-12">
        <!-- Chart 1 -->
        <div class="h-100 d-flex flex-column" style="width: 100%;">
          <!-- Row: title -->
          <div
            class="row f-row mb-2 markets-chart-title d-flex align-items-center"
          >
            <div class="col-6" style="font-size: 1rem">
              <div v-if="isCurrency">
                Correlation Matrix of {{ currencyString.currency_name }} Returns
                (Last 12 Months)
              </div>
              <div v-else>
                <span style="color: var(--textinactive)">{{
                  currencyString.fk_idproduct.toUpperCase()
                }}</span>
                Return Correlation (&#961;) Matrix
              </div>
            </div>
            <slot name="spot"></slot>
          </div>
          <!-- Row: Chart -->
          <div class="row f-row h-100">
            <div
              class="col col-12 text-center progress-div"
              style="top: 45%!important;"
              v-if="showLoading"
            >
              <QSLoading
                :isLoading="showLoading"
                :size="35"
                :colorTheme="colorTheme"
              />
            </div>
            <div
              class="col col-12 text-center"
              v-else-if="correlation.length === 0"
              style="position: absolute; left: 0; top: 47.5%; z-index: 10000000;"
            >
              No Data Available
            </div>
            <div class="col col-3" style="height:100%; width:100%">
              <div
                class="h-100 d-flex flex-column"
                id="correlation_txt-div"
                style="width: 100%;"
              >
                <svg id="svg-correlation_txt"></svg>
              </div>
            </div>
            <div class="col col-9" style="height:100%; width:100%">
              <div
                class="h-100 d-flex flex-column"
                id="correlation-div"
                style="width: 100%;"
              >
                <svg id="svg-correlation"></svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { D3Chart } from "@/_core/charts/D3Chart";
import { format, select } from "d3";
import { getCorrelations } from "@/_core/data/api_calls/correlations/getCorrelations";
import {
  colorSelectionVol,
  darkBackground2,
  textactive
} from "@/_core/_helpers/_parametric_tools";
import QSLoading from "@/components/QSLoading.vue";

export default {
  name: "Correlations",
  components: { QSLoading },
  props: {
    currencyNumber: { required: true },
    currencyString: { required: true },
    isCurrency: { required: true },
    contract: { required: true },
    colorTheme: { required: true, type: Boolean, default: true },
    // ============ Data Classes ===============
    processData: { required: true }
  },
  data() {
    return {
      showLoading: false,
      D3Chart: null,
      correlation: [],
      correlColumns: [],
      diagonal: [],
      prices: [],
      change: [],
      screenWidth: 0,
      svgNodeCorr: null,
      svgNodeCorrTxt: null,
      currencyFormat: ",.3r",
      formatPercent: format(",.2%"),
      color: [],
      colorSel: JSON.parse(
        JSON.stringify(colorSelectionVol(this.colorTheme))
      ).reverse()
    };
  },
  created() {
    this.D3ChartCorrelation = new D3Chart("correlation-div", "svg-correlation");
    this.D3ChartCorrelationTxt = new D3Chart(
      "correlation_txt-div",
      "svg-correlation_txt"
    );
    let asset = this.isCurrency ? "currency" : "commodity";
    getCorrelations(this, asset, {
      commodityNumber: this.currencyNumber,
      contract: this.contract
    });
  },
  mounted() {
    this.colorSel = JSON.parse(
      JSON.stringify(colorSelectionVol(this.colorTheme))
    ).reverse();
    this.color = this.processData.setD3ColorFunction(this.colorSel, [
      -1,
      -0.75,
      -0.5,
      -0.25,
      0,
      0.25,
      0.5,
      0.75,
      1
    ]);
    let _this = this;
    this.updateScreenWidth();
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
  },
  watch: {
    colorTheme() {
      this.colorSel = JSON.parse(
        JSON.stringify(colorSelectionVol(this.colorTheme))
      ).reverse();
      this.color = this.processData.setD3ColorFunction(this.colorSel, [
        -1,
        -0.75,
        -0.5,
        -0.25,
        0,
        0.25,
        0.5,
        0.75,
        1
      ]);
      this.callWidth();
    },
    showLoading() {
      if (this.showLoading === false) {
        this.addNames();
        this.getColumn();
        this.renderCorrelationsMethod();
        this.renderCorrelationTxtMethod();
      } else {
        select("#correlation-div")
          .select("svg > *")
          .remove();
        select("#correlation_txt-div")
          .select("svg > *")
          .remove();
      }
    },
    currencyString() {
      this.getColumn();
      let asset = this.isCurrency ? "currency" : "commodity";
      getCorrelations(this, asset, {
        commodityNumber: this.currencyString.pk_idcommodity,
        contract: this.currencyString.selected_contract
      });
      this.renderCorrelationsMethod();
      this.renderCorrelationTxtMethod();
    }
  },
  methods: {
    // ==================== helpers ====================
    getColumn() {
      let name;
      if (this.isCurrency) {
        name = this.currencyString.currency_name.toLowerCase();
      } else {
        name =
          this.currencyString.fk_idproduct.toLowerCase() + "_" + this.contract;
      }
      let _this = this;
      this.correlColumns.forEach(function(d) {
        if (name.indexOf(d) > -1) {
          _this.selectedColumn = d;
        }
      });
    },
    addNames() {
      this.correlColNames = this.correlColumns;
      this.correlLongNames = this.correlColumns;
      // TODO: ver MarketsRelativeValues si tenemos otros nombres para mostrar
    },
    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    },
    // ================= render charts ====================
    renderCorrelationTxtMethod() {
      this.svgNodeCorrTxt = this.D3ChartCorrelationTxt.setSVG(
        0.5,
        20,
        0.5,
        10,
        0.5,
        20,
        0.8,
        30,
        this.colorTheme
      );
      // === set axis
      this.svgNodeCorrTxt = this.D3ChartCorrelationTxt.setBandAxis(
        this.svgNodeCorrTxt,
        [0, 1, 2, 3],
        JSON.parse(JSON.stringify(this.correlColumns)).reverse(),
        [0, 1, 2, 3],
        JSON.parse(JSON.stringify(this.correlColNames)).reverse(),
        false,
        false
      );
      // === texts
      let _this = this;
      let data = [];
      this.correlColumns.forEach(function(d, i) {
        if (i === 0) {
          data.push({
            x: 0,
            y: d,
            name: ""
          });
        } else {
          data.push({
            x: 0,
            y: d,
            name: _this.correlColNames[i].toUpperCase() + ":"
          });
        }
      });
      this.correlColumns.forEach(function(d, i) {
        if (i === 0) {
          data.push({
            x: 1,
            y: d,
            name: "LAST"
          });
        } else {
          data.push({
            x: 1,
            y: d,
            name: format(_this.currencyFormat)(_this.prices[i])
          });
        }
      });
      this.correlColumns.forEach(function(d, i) {
        if (i === 0) {
          data.push({
            x: 2,
            y: d,
            name: "CHANGE"
          });
        } else {
          data.push({
            x: 2,
            y: d,
            name: _this.formatPercent(_this.change[i])
          });
        }
      });
      this.correlColumns.forEach(function(d, i) {
        if (i === 0) {
          data.push({
            x: 3,
            y: d,
            name: "CORREL"
          });
        } else {
          data.push({
            x: 3,
            y: d,
            name: format(_this.currencyFormat)(_this.diagonal[i].correl)
          });
        }
      });
      // horizontal lines and title area
      this.svgNodeCorrTxt
        .append("rect")
        .attr("fill", darkBackground2(this.colorTheme))
        .attr("x", 5)
        .attr("y", 0)
        .attr("height", this.svgNodeCorrTxt.y.bandwidth())
        .attr("width", this.svgNodeCorrTxt.width);
      for (var i = 0; i < this.correlColumns.length; i++) {
        this.svgNodeCorrTxt
          .append("line")
          .attr("class", "charts-tick-line")
          .attr("x1", 5)
          .attr("y1", this.svgNodeCorrTxt.y(this.correlColumns[i]))
          .attr("x2", this.svgNodeCorrTxt.width)
          .attr("y2", this.svgNodeCorrTxt.y(this.correlColumns[i]));
      }
      this.svgNodeCorrTxt
        .append("line")
        .attr("class", "charts-tick-line")
        .attr("x1", 5)
        .attr("y1", this.svgNodeCorrTxt.height)
        .attr("x2", this.svgNodeCorrTxt.width)
        .attr("y2", this.svgNodeCorrTxt.height);
      this.D3ChartCorrelationTxt.text(
        this.svgNodeCorrTxt,
        data,
        "x",
        "y",
        "name",
        "correl",
        this.isCurrency
          ? "0.8rem"
          : this.screenWidth < 1200
          ? "0.6rem"
          : "0.7rem",
        !this.isCurrency
          ? `${this.svgNodeCorrTxt.x.bandwidth() + 5}`
          : `${this.svgNodeCorrTxt.x.bandwidth() - 5}`,
        this.svgNodeCorrTxt.y.bandwidth() / 2,
        "end",
        "central",
        0,
        textactive(this.colorTheme),
        false
      );
    },
    renderCorrelationsMethod() {
      this.svgNodeCorr = this.D3ChartCorrelation.setSVG(
        0.5,
        20,
        0.5,
        115,
        0.5,
        20,
        0.5,
        5,
        this.colorTheme
      );
      // === set axis
      this.svgNodeCorr = this.D3ChartCorrelation.setBandAxis(
        this.svgNodeCorr,
        this.correlColumns,
        JSON.parse(JSON.stringify(this.correlColumns)).reverse(),
        this.correlColNames,
        JSON.parse(JSON.stringify(this.correlColNames)).reverse(),
        false,
        false
      );
      // === paint rects
      let opacity = 0.95;
      this.D3ChartCorrelation.correlation(
        this.svgNodeCorr,
        this.correlation,
        this.correlLongNames,
        this.color,
        "RVCORR",
        "x",
        "y",
        "correl",
        1,
        opacity,
        format(this.currencyFormat),
        this.selectedColumn,
        this.colorTheme
      );
      // === texts
      let _this = this;
      let data = [];
      this.correlColumns.forEach(function(d, i) {
        data.push({ x: d, name: _this.correlColNames[i].toUpperCase() });
      });
      this.D3ChartCorrelation.text(
        this.svgNodeCorr,
        data,
        "x",
        "x",
        "name",
        "correl",
        "0.8rem",
        this.svgNodeCorr.x.bandwidth() / 2,
        this.svgNodeCorr.y.bandwidth() / 2,
        "middle",
        "central",
        0,
        textactive(this.colorTheme),
        false
      );
      // add the vertical color legend
      let legendMargin = {
        top: 0,
        bottom: 40,
        left: 0,
        right: 30
      };
      this.D3ChartCorrelation.vColorLegend(
        this.svgNodeCorr,
        [-1, -0.75, -0.5, -0.25, 0, 0.25, 0.5, 0.75, 1].map(d =>
          _this.color(d)
        ),
        [-1, -0.75, -0.5, -0.25, 0, 0.25, 0.5, 0.75, 1],
        legendMargin,
        [-1, 1],
        50,
        opacity,
        ".1f"
      );
    },
    callWidth() {
      this.updateScreenWidth();
      // Event listener that triggers chart rendering on size change
      this.renderCorrelationsMethod();
      this.renderCorrelationTxtMethod();
    }
    // ================== API calls ====================
  }
};
</script>

<style scoped></style>
